import React, { useRef, useEffect } from 'react';
import { AuditTimelineEvent } from '../../../interfaces/Audit';
import { useEsteiras } from '../../../hooks/useEsteiras';
import './PlanningComparisonModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface PlanningComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: AuditTimelineEvent | null;
}

const PlanningComparisonModal: React.FC<PlanningComparisonModalProps> = ({
  isOpen,
  onClose,
  event,
}) => {
  const { labelDe } = useEsteiras();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleMouseDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const metadata = event.metadata || {};
  const statuses: string[] = metadata.statuses || [];
  const previousStatuses: string[] = metadata.previousStatuses || [];
  const addedStatuses: string[] = metadata.addedStatuses || [];
  const removedStatuses: string[] = metadata.removedStatuses || [];
  // at-66 (BUG-V15): esteiras que o Chefe tirou da seleção mas o sistema MANTEVE, porque
  // têm serviço em aberto (at-46 — serviço é fato, não plano). O backend já gravava isso
  // no registro; sem exibição, o modal mostrava a esteira como se ela seguisse no plano
  // por escolha de quem editou.
  const preservadasStatuses: string[] = metadata.preservadasStatuses || [];

  const performer = event.collaboratorNames.length > 0
    ? event.collaboratorNames.join(', ')
    : metadata.performedByName || 'Sistema';

  const dateStr = new Date(event.occurredAt).toLocaleDateString('pt-BR');
  const timeStr = new Date(event.occurredAt).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const hasPrevious = previousStatuses.length > 0;

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={modalRef}>
        <div className="planning-modal-header">
          <h2>Histórico do Planejamento de Esteira</h2>
          <button className="close-button" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="planning-modal-metadata">
          <div className="metadata-item">
            <strong>Alterado por:</strong> <span>{performer}</span>
          </div>
          <div className="metadata-item">
            <strong>Data da alteração:</strong> <span>{dateStr} às {timeStr}</span>
          </div>
        </div>

        <div className="planning-modal-body">
          {!metadata.statuses && (
            <div className="planning-modal-fallback">
              <p className="fallback-title">{event.title}</p>
              <p className="fallback-desc">{event.description || 'Sem descrição complementar.'}</p>
              <p className="fallback-note">
                Este registro refere-se a uma alteração individual e não contém o histórico do fluxo completo.
              </p>
            </div>
          )}

          {metadata.statuses && (
            <div className={`planning-comparison-grid ${hasPrevious ? 'has-split' : 'single-column'}`}>
              {hasPrevious && (
                <div className="planning-column">
                  <div className="column-header">Planejamento Anterior</div>
                  <div className="status-flow-list">
                    {previousStatuses.map((status, index) => {
                      const isRemoved = removedStatuses.includes(status);
                      return (
                        <div
                          key={`prev-${status}-${index}`}
                          className={`status-flow-item ${isRemoved ? 'item-removed' : 'item-unchanged'}`}
                        >
                          <div className="item-index">{index + 1}</div>
                          <div className="item-label">
                            {labelDe(status) || status}
                          </div>
                          {isRemoved && <span className="badge-change font-removed">Removido</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="planning-column">
                <div className="column-header">Novo Planejamento</div>
                <div className="status-flow-list">
                  {statuses.map((status, index) => {
                    const isAdded = addedStatuses.includes(status);
                    return (
                      <div
                        key={`new-${status}-${index}`}
                        className={`status-flow-item ${isAdded ? 'item-added' : 'item-unchanged'}`}
                      >
                        <div className="item-index">{index + 1}</div>
                        <div className="item-label">
                          {labelDe(status) || status}
                        </div>
                        {isAdded && <span className="badge-change font-added">Adicionado</span>}
                      </div>
                    );
                  })}
                </div>

                {preservadasStatuses.length > 0 && (
                  <div style={{ marginTop: '16px', padding: '12px 16px', background: 'var(--tema-fundo, #fffbeb)', border: '1px solid var(--tema-borda-amarelo, #fde68a)', borderRadius: '12px' }}>
                    <strong style={{ display: 'block', fontSize: '13px', color: 'var(--tema-texto-laranja, #78350f)', fontFamily: 'AmpleSoft-Bold, sans-serif', marginBottom: '6px' }}>
                      Mantidas por serviço em aberto:
                    </strong>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13.5px', color: 'var(--tema-texto-laranja, #78350f)' }}>
                      {preservadasStatuses.map((status, index) => (
                        <li key={`preservada-${status}-${index}`} style={{ marginBottom: '2px' }}>
                          {labelDe(status) || status}
                        </li>
                      ))}
                    </ul>
                    <span style={{ display: 'block', marginTop: '6px', fontSize: '12.5px', color: 'var(--tema-texto-laranja, #92400e)' }}>
                      Estas etapas foram retiradas da seleção, mas continuam no planejamento porque o serviço
                      delas está em andamento ou ficou inacabado. Para removê-las, conclua o serviço antes.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanningComparisonModal;
